import { useState } from 'react';
import { TIER_PRICING } from '@vcard/shared';
import { useAppTranslation } from '../hooks/useAppTranslation.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { euro, seatUnitCents } from '../lib/seatPrices.js';

interface Props {
  seats: { starter: number; business: number; usedStarter: number; usedBusiness: number; interval: 'month' | 'year' | null };
  userCount: number;
  isOwner: boolean;
  /** the signed-in user holds a Starter seat */
  onStarter: boolean;
  onChanged: () => void;
}

type Scope = 'self' | 'all';

/**
 * Move to Business after a confirmation. A Starter user upgrades themselves; the owner can choose between
 * only themselves or all Starter users, with the combined price shown for each choice.
 */
export function SelfUpgrade({ seats, userCount, isOwner, onStarter, onChanged }: Props) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [scope, setScope] = useState<Scope>('self');

  const unit = seatUnitCents('business', seats.interval);
  const unitStarter = seatUnitCents('starter', seats.interval);
  const per = seats.interval === 'month' ? t('plans.perMonth', '/ Monat') : t('plans.perYear', '/ Jahr');
  const freeBusiness = seats.business - seats.usedBusiness;

  // Options: only me (needs a free Business seat or books one) or everybody (all users on Business)
  const selfDelta = onStarter ? (freeBusiness > 0 ? 0 : unit) : 0;
  const allBusiness = Math.max(seats.business, userCount);
  const allDelta = (allBusiness - seats.business) * unit - seats.starter * unitStarter;
  const canChooseAll = isOwner && seats.usedStarter > (onStarter ? 1 : 0);
  const effectiveScope: Scope = canChooseAll ? scope : 'self';
  const delta = effectiveScope === 'all' ? allDelta : selfDelta;

  async function confirm() {
    setBusy(true);
    setError('');
    try {
      const res = effectiveScope === 'all'
        ? await fetch('/api/billing/seats/assign-all', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ tier: 'business', buyMissing: true, releaseOther: true }),
          })
        : await fetch('/api/billing/seats/self', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
            body: JSON.stringify({ tier: 'business', buyIfNeeded: true }),
          });
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (res.ok) { setOpen(false); onChanged(); }
      else setError(body.error ?? `HTTP ${res.status}`);
    } finally {
      setBusy(false);
    }
  }

  const money = (cents: number) => (cents === 0 ? t('self.nothingExtra', 'ohne Mehrkosten') : `${cents > 0 ? '+' : '−'}${euro(cents)} ${per}`);

  return (
    <>
      <button type="button" onClick={() => { setError(''); setScope(canChooseAll ? 'all' : 'self'); setOpen(true); }}
        className="rounded-full bg-accent-600 px-4 py-2 text-sm font-semibold text-white hover:bg-accent-500 transition-colors whitespace-nowrap">
        {onStarter ? t('self.upgrade', 'Auf Business wechseln') : t('self.upgradeStarters', 'Starter-Nutzer upgraden')}
      </button>
      <ConfirmDialog
        open={open}
        title={canChooseAll ? t('self.titleChoose', 'Wer soll auf Business wechseln?') : t('self.title', 'Auf Business wechseln?')}
        confirmText={busy ? t('plans.loading', 'Lädt…') : t('self.confirm', 'Ja, wechseln')}
        cancelText={canChooseAll ? t('self.cancelNone', 'Nein, nichts ändern') : t('seats.cancel', 'Abbrechen')}
        busy={busy}
        onClose={() => setOpen(false)}
        onConfirm={confirm}
      >
        <p>{t('self.intro', 'Du bekommst Analytics, Leads, Visitenkarten-Scan und Tracking-Links.')}</p>
        {canChooseAll && (
          <fieldset className="space-y-2">
            <legend className="sr-only">{t('self.titleChoose', 'Wer soll auf Business wechseln?')}</legend>
            {([
              ['all', t('self.scopeAll', 'Alle {{count}} Nutzer', { count: userCount }), allDelta],
              ...(onStarter ? [['self', t('self.scopeSelf', 'Nur ich'), selfDelta] as [Scope, string, number]] : []),
            ] as Array<[Scope, string, number]>).map(([value, text, cents]) => (
              <label key={value} className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2.5 ${effectiveScope === value ? 'border-accent-500 bg-accent-100 dark:bg-accent-500/10' : 'border-line'}`}>
                <span className="flex items-center gap-2 text-ink">
                  <input type="radio" name="upgrade-scope" checked={effectiveScope === value} onChange={() => setScope(value)} />
                  {text}
                </span>
                <span className="tabular-nums text-ink">{money(cents)}</span>
              </label>
            ))}
          </fieldset>
        )}
        {effectiveScope === 'self' && (
          freeBusiness > 0 ? (
            <p className="text-ink">{t('self.free', 'Es ist noch ein Business-Platz frei, er kostet nichts extra.')}</p>
          ) : (
            <p className="text-ink">
              {t('self.buy', 'Es ist kein Business-Platz frei. Ein Platz wird dazugebucht: {{price}} {{per}}, im laufenden Zeitraum anteilig berechnet. Das erscheint auf der Rechnung deiner Organisation.', {
                price: euro(unit), per,
              })}
            </p>
          )
        )}
        {effectiveScope === 'all' && (
          <p className="text-ink">
            {t('self.allSummary', 'Gesamt: {{amount}}, im laufenden Zeitraum anteilig berechnet. Nicht mehr benötigte Starter-Plätze werden gekündigt, das ist schon eingerechnet.', { amount: money(delta) })}
          </p>
        )}
        {onStarter && effectiveScope === 'self' && <p>{t('self.note', 'Dein bisheriger {{plan}}-Platz wird frei.', { plan: TIER_PRICING.starter.label })}</p>}
        {error && <p role="alert" className="text-danger-600 dark:text-danger-300">{error}</p>}
      </ConfirmDialog>
    </>
  );
}
